// src/services/BigQueryService.js
// Queries BigQuery log data via the webhook backend API.
// Replaces all direct Firestore reads in WebhookLogs.js.

import { getAuth } from 'firebase/auth';

const WEBHOOK_BACKEND_URL = process.env.REACT_APP_WEBHOOK_BACKEND_URL
  || "https://resido-backend-webhook-744649253655.asia-south1.run.app";

/**
 * Get Firebase Auth token for API requests.
 */
async function getAuthToken() {
  const user = getAuth().currentUser;
  if (!user) throw new Error("Not authenticated");
  return user.getIdToken();
}

/**
 * Generic log query — maps to POST /api/v2/logs/query
 * @param {string} table - BigQuery table name
 * @param {object} options - Query options
 * @returns {Promise<Array>} Array of row objects
 */
export async function fetchLogs(table, {
  instituteId = "",
  hostel = "",
  startDate = "",
  endDate = "",
  filters = {},
  orderBy = "timestamp",
  orderDir = "DESC",
  limit = 100,
  offset = 0
} = {}) {
  const token = await getAuthToken();
  const resp = await fetch(`${WEBHOOK_BACKEND_URL}/api/v2/logs/query`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`
    },
    body: JSON.stringify({
      table,
      institute_id: instituteId,
      hostel,
      start_date: startDate,
      end_date: endDate,
      filters,
      order_by: orderBy,
      order_dir: orderDir,
      limit,
      offset
    })
  });

  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error(err.error || `Query failed: ${resp.status}`);
  }

  const result = await resp.json();
  return result.data || [];
}

/**
 * Aggregated payment metrics — maps to POST /api/v2/logs/metrics
 */
export async function fetchMetrics({
  instituteId = "",
  hostel = "",
  startDate = "",
  endDate = ""
} = {}) {
  const token = await getAuthToken();
  const resp = await fetch(`${WEBHOOK_BACKEND_URL}/api/v2/logs/metrics`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`
    },
    body: JSON.stringify({
      institute_id: instituteId,
      hostel,
      start_date: startDate,
      end_date: endDate
    })
  });

  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error(err.error || `Metrics query failed: ${resp.status}`);
  }

  const result = await resp.json();
  return result.data || [];
}

/**
 * Log an admin action — maps to POST /api/v2/logs/admin
 */
export async function logAdminAction(action, details = "", instituteId = "", hostel = "") {
  const token = await getAuthToken();
  const resp = await fetch(`${WEBHOOK_BACKEND_URL}/api/v2/logs/admin`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`
    },
    body: JSON.stringify({ action, details, institute_id: instituteId, hostel })
  });

  if (!resp.ok) {
    console.error("Failed to log admin action:", resp.status);
  }
}

/**
 * Fetch admin logs — maps to GET /api/v2/logs/admin
 */
export async function fetchAdminLogs({
  instituteId = "",
  adminEmail = "",
  startDate = "",
  endDate = "",
  limit = 100,
  offset = 0
} = {}) {
  const token = await getAuthToken();
  const params = new URLSearchParams({
    institute_id: instituteId,
    admin_email: adminEmail,
    start_date: startDate,
    end_date: endDate,
    limit: String(limit),
    offset: String(offset)
  });

  const resp = await fetch(`${WEBHOOK_BACKEND_URL}/api/v2/logs/admin?${params}`, {
    headers: { "Authorization": `Bearer ${token}` }
  });

  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error(err.error || `Admin logs query failed: ${resp.status}`);
  }

  const result = await resp.json();
  return result.data || [];
}

/**
 * Clear all rows from specified BigQuery tables — maps to DELETE /api/v2/logs/clear
 * @param {string[]} tables - Table names to clear, or ["all"] for all tables
 * @returns {Promise<object>} { status, results, total_deleted, failed_tables }
 */
export async function clearBigQueryTables(tables = ["all"]) {
  const token = await getAuthToken();
  const resp = await fetch(`${WEBHOOK_BACKEND_URL}/api/v2/logs/clear`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`
    },
    body: JSON.stringify({ tables })
  });

  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error(err.error || `Clear operation failed: ${resp.status}`);
  }

  return resp.json();
}
